import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { localTime } from "@/lib/time";
import { ago } from "@/components/FleetTrailers";

// Top-bar "Refresh from Alvys": rebuilds the saved load cache, shows last refresh time, polls while running.
export const AlvysRefresh = () => {
  const [s, setS] = useState(null);
  const prev = useRef(null);
  const load = useCallback(() => api.get("/refresh/status").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden || s?.running) load(); }, s?.running ? 10000 : 300000);
    return () => clearInterval(t);
  }, [s?.running, load]);
  useEffect(() => {
    if (prev.current?.running && s && !s.running) {
      if (s.last_sync !== prev.current.last_sync) toast.success(`Alvys refresh done: ${(s.total_loads || 0).toLocaleString()} loads`, { action: { label: "Reload page", onClick: () => window.location.reload() } });
      else if (s.last_error) toast.error(`Alvys refresh failed: ${s.last_error}`);
    }
    prev.current = s;
  }, [s]);
  const start = () => api.post("/refresh/alvys").then((r) => { setS(r.data); toast.info(r.data.accepted ? "Refreshing loads from Alvys. This takes a few minutes." : "A refresh is already running."); })
    .catch((e) => toast.error(formatApiError(e)));
  if (!s) return null;
  const failed = s.last_error && (!s.last_sync || (s.error_at || "") > s.last_sync);
  return (
    <button type="button" data-testid="alvys-refresh-btn" onClick={start} disabled={s.running}
      title={`Last Alvys refresh: ${s.last_sync ? localTime(s.last_sync) : "never"}${s.total_loads ? ` · ${s.total_loads.toLocaleString()} loads` : ""}${failed ? `\nLast attempt failed: ${s.last_error}` : ""}`}
      className="flex items-center gap-1.5 rounded-lg border border-slate-700/50 bg-slate-800/60 px-3 py-1.5 text-[11px] font-semibold text-slate-300 transition-colors hover:bg-slate-700/60 disabled:cursor-wait disabled:opacity-80">
      {failed && !s.running ? <AlertTriangle className="h-3.5 w-3.5 text-amber-400" /> : <RefreshCw className={`h-3.5 w-3.5 text-blue-400 ${s.running ? "animate-spin" : ""}`} />}
      <span data-testid="alvys-refresh-label">{s.running ? "Refreshing Alvys…" : `Alvys · ${s.last_sync ? ago(s.last_sync) : "never"}`}</span>
    </button>
  );
};
